import React, { useMemo, useState } from "react";
import {
	getTrainingFull,
	getStudentExercises,
	createExercise,
	createTraining,
} from "../../services/authService";

// Résout les exos d'un template contre le catalogue d'un élève, en créant
// ceux qui manquent. Renvoie la liste des exos remappés (idExercice = id chez l'élève).
async function resolveExosForStudentDirect(studentId, templateExos) {
	const normalize = (s) => (s || "").trim().toLowerCase();
	const catRes = await getStudentExercises(studentId);
	const catalog = catRes.success ? (catRes.exercises || []) : [];
	const byName = new Map();
	catalog.forEach((e) => {
		const k = normalize(e.nomExercice);
		if (k) byName.set(k, e);
	});
	const toCreate = new Map();
	for (const ex of templateExos) {
		const name = ex.nom || ex.nomExercice;
		if (!name) continue;
		const k = normalize(name);
		if (!byName.has(k) && !toCreate.has(k)) {
			toCreate.set(k, {
				nom: name,
				description: ex.comExercice || ex.description || "",
				muscles: ex.muscles || [],
			});
		}
	}
	for (const item of toCreate.values()) {
		const created = await createExercise(studentId, item.nom, item.description, item.muscles);
		if (!created.success) return { ok: false, error: `Création exo « ${item.nom} » échouée` };
		byName.set(normalize(item.nom), { id: created.id, nomExercice: item.nom });
	}
	const resolved = templateExos.map((ex) => {
		const name = ex.nom || ex.nomExercice;
		const ref = byName.get(normalize(name));
		return { ...ex, idExercice: ref ? ref.id : ex.idExercice };
	});
	return { ok: true, exos: resolved };
}

function fmtInput(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

export default function BulkAssignTemplateModal({
	coachOwnerId,          // = user.id pour recharger le template
	coachTemplates,        // liste des templates coach
	students,              // liste des élèves
	initialTemplateId = null,
	onClose,
	onDone,
	onNotif,
}) {
	const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId);
	const [templateSearch, setTemplateSearch] = useState("");
	const [selectedStudentIds, setSelectedStudentIds] = useState([]);
	const [dates, setDates] = useState([]); // timestamps
	const [newDate, setNewDate] = useState(fmtInput(new Date()));
	const [progress, setProgress] = useState(null); // {done, total}
	const [error, setError] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const filteredTemplates = useMemo(() => {
		const q = templateSearch.trim().toLowerCase();
		return (coachTemplates || []).filter((t) => {
			const nom = (t.nom ?? t.Nom ?? "").toLowerCase();
			return !q || nom.includes(q);
		});
	}, [coachTemplates, templateSearch]);

	const selectedTemplate = (coachTemplates || []).find((t) => (t.Id ?? t.id) === selectedTemplateId) || null;

	const toggleStudent = (id) => {
		setSelectedStudentIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
	};

	const selectAllStudents = () => setSelectedStudentIds((students || []).map((s) => s.id));
	const clearStudents = () => setSelectedStudentIds([]);

	const addDate = () => {
		if (!newDate) return;
		const d = new Date(newDate + "T12:00:00").getTime();
		if (dates.includes(d)) return;
		setDates((prev) => [...prev, d].sort((a, b) => a - b));
	};

	const removeDate = (ts) => setDates((prev) => prev.filter((d) => d !== ts));

	const total = selectedStudentIds.length * dates.length;
	const canSubmit = selectedTemplate && selectedStudentIds.length > 0 && dates.length > 0 && !submitting;

	const handleConfirm = async () => {
		if (!canSubmit) return;
		setError("");
		setSubmitting(true);

		// 1. Fetch template once
		const templateId = selectedTemplate.Id ?? selectedTemplate.id;
		const templateNom = selectedTemplate.Nom ?? selectedTemplate.nom ?? "Séance";
		const full = await getTrainingFull(coachOwnerId, templateId);
		if (!full.success) {
			setSubmitting(false);
			setError("Impossible de charger le template");
			return;
		}
		const templateExos = full.training.exercices || [];

		// 2. For each student, resolve exos ONCE (avoids re-creating per date)
		let done = 0;
		let ok = 0;
		let fail = 0;
		setProgress({ done: 0, total });

		for (const studentId of selectedStudentIds) {
			const resolved = await resolveExosForStudentDirect(studentId, templateExos);
			if (!resolved.ok) {
				fail += dates.length;
				done += dates.length;
				setProgress({ done, total });
				continue;
			}
			let ordre = 0;
			const exercicesForApi = resolved.exos.map((ex) => ({
				Id: -1,
				LocalId: Date.now() + ordre,
				IdExercice: ex.idExercice,
				Supprime: 0,
				Ordre: ordre++,
				SupersetGroup: ex.supersetGroup || 0,
				ComExercice: ex.comExercice || "",
				ListSerie: (ex.series || []).map((s) => ({ reps: s.reps || 0, poids: s.poids || 0, poidsDec: s.poidsDec || 0, rpe: s.rpe || 0 })),
				ListSerieEchauffement: (ex.warmupSeries || []).map((w) => ({ reps: w.reps || 0, poids: w.poids || 0, poidsDec: w.poidsDec || 0 })),
			}));

			for (const dateTs of dates) {
				const res = await createTraining(studentId, templateNom, dateTs, false, exercicesForApi);
				if (res.success) ok++;
				else fail++;
				done++;
				setProgress({ done, total });
			}
		}

		setSubmitting(false);
		if (fail === 0) {
			onNotif?.(`${ok} séance${ok > 1 ? "s" : ""} créée${ok > 1 ? "s" : ""}`);
			onDone?.();
			onClose?.();
		} else {
			setError(`${ok} créées, ${fail} en échec`);
			if (ok > 0) onDone?.();
		}
	};

	return (
		<div className="mens-modal-overlay" onClick={submitting ? undefined : onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, width: "min(720px, 95vw)" }}>
				<div className="mens-modal-header">
					<h3>Affecter un template</h3>
					<button className="mens-modal-close" onClick={onClose} disabled={submitting}>×</button>
				</div>

				{/* Étape 1 : Template */}
				<div style={{ marginTop: 12 }}>
					<div className="bulk-assign-step">
						<span className="bulk-assign-step-num">1</span>
						<span className="bulk-assign-step-label">Template</span>
					</div>
					<input
						type="text"
						value={templateSearch}
						onChange={(e) => setTemplateSearch(e.target.value)}
						placeholder="Rechercher un template…"
						className="mens-modal-input"
					/>
					<div style={{ maxHeight: 160, overflowY: "auto", marginTop: 6, border: "1px solid var(--line-soft)", borderRadius: 8, padding: 4 }}>
						{filteredTemplates.length === 0 ? (
							<div style={{ padding: 12, fontSize: 12, color: "var(--ink-faint)" }}>Aucun template</div>
						) : filteredTemplates.map((t) => {
							const id = t.Id ?? t.id;
							const nom = t.Nom ?? t.nom ?? "Sans nom";
							const exos = t.ListExercice ?? t.listExercice ?? t.exercices ?? [];
							return (
								<button
									key={id} type="button"
									onClick={() => setSelectedTemplateId(id)}
									className={`bulk-assign-template-item ${selectedTemplateId === id ? "active" : ""}`}
								>
									<span style={{ fontWeight: 600 }}>{nom}</span>
									<span style={{ fontSize: 11, color: "var(--ink-soft)" }}>{exos.length} exo{exos.length > 1 ? "s" : ""}</span>
								</button>
							);
						})}
					</div>
				</div>

				{/* Étape 2 : Élèves */}
				<div style={{ marginTop: 16 }}>
					<div className="bulk-assign-step">
						<span className="bulk-assign-step-num">2</span>
						<span className="bulk-assign-step-label">Élèves ({selectedStudentIds.length})</span>
						<div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
							<button type="button" className="bulk-assign-mini-btn" onClick={selectAllStudents}>Tous</button>
							<button type="button" className="bulk-assign-mini-btn" onClick={clearStudents}>Aucun</button>
						</div>
					</div>
					<div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid var(--line-soft)", borderRadius: 8, padding: 4 }}>
						{(students || []).map((s) => {
							const active = selectedStudentIds.includes(s.id);
							return (
								<button
									key={s.id} type="button"
									onClick={() => toggleStudent(s.id)}
									className={`bulk-assign-student-item ${active ? "active" : ""}`}
								>
									<span className="cbox">{active ? "☑" : "☐"}</span>
									<span>{s.prenom} {s.nom}</span>
								</button>
							);
						})}
					</div>
				</div>

				{/* Étape 3 : Dates */}
				<div style={{ marginTop: 16 }}>
					<div className="bulk-assign-step">
						<span className="bulk-assign-step-num">3</span>
						<span className="bulk-assign-step-label">Dates ({dates.length})</span>
					</div>
					<div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
						<input
							type="date"
							value={newDate}
							onChange={(e) => setNewDate(e.target.value)}
							className="mens-modal-input"
							style={{ flex: 1 }}
						/>
						<button type="button" className="mens-btn ghost" onClick={addDate}>+ Ajouter</button>
					</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
						{dates.length === 0 ? (
							<div style={{ fontSize: 12, color: "var(--ink-faint)" }}>Aucune date sélectionnée</div>
						) : dates.map((ts) => (
							<span key={ts} className="bulk-assign-date-chip">
								{new Date(ts).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}
								<button type="button" onClick={() => removeDate(ts)}>×</button>
							</span>
						))}
					</div>
				</div>

				{/* Récap */}
				<div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: "rgba(48,164,154,0.06)", border: "1px solid rgba(48,164,154,0.2)", fontSize: 13, color: "var(--ink)" }}>
					{total > 0
						? <>Vous allez créer <strong>{total}</strong> séance{total > 1 ? "s" : ""} ({selectedStudentIds.length} élève{selectedStudentIds.length > 1 ? "s" : ""} × {dates.length} date{dates.length > 1 ? "s" : ""})</>
						: <>Choisissez un template, des élèves et des dates.</>
					}
				</div>

				{progress && submitting && (
					<div style={{ marginTop: 8, fontSize: 12, color: "var(--ink-soft)" }}>
						Progression : {progress.done} / {progress.total}
					</div>
				)}
				{error && <div className="mens-modal-error" style={{ marginTop: 8 }}>{error}</div>}

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={submitting}>Annuler</button>
					<button className="mens-btn primary" onClick={handleConfirm} disabled={!canSubmit}>
						{submitting ? "En cours…" : (total > 0 ? `Créer ${total} séance${total > 1 ? "s" : ""}` : "Créer")}
					</button>
				</div>
			</div>
		</div>
	);
}
